import type { VidSrc } from '@archipro-design/aria';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useStyles, VideoPlayer } from '@archipro-design/aria';
import { useLatest } from 'ahooks';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import { getThumbnailImage } from '@modules/product/util/get-thumbnail-image';
import * as S from './VideoSlide.style';
import type { LibraryVideo } from '@modules/product/type/product-gallery-types';
import useHiResImage from '@modules/product/hook/use-hi-res-image';

import { PRODUCT_GALLERY_SIZE } from './ProductGallery.style';
import { useTracker } from '@archipro-website/tracker';

interface VideoSlideProps {
    libraryVideo: LibraryVideo;
}

const VideoSlide = ({ libraryVideo }: VideoSlideProps) => {
    const [shouldPlay, setShouldPlay] = React.useState(false);
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();

    const tracker = useTracker();

    const getImageRef = useLatest(getThumbnailImage);

    const { image } = getImageRef.current(libraryVideo);
    const { VideoID, Type } = libraryVideo;
    const imgSrc = image.Desktop ?? '';

    const { hiResImage, fallback } = useHiResImage(imgSrc);

    const [observerRef, inView] = useInView({
        rootMargin: '50% 0px',
    });
    React.useEffect(() => {
        if (!inView) {
            setShouldPlay(false);
        }
    }, [inView]);

    const extraVideoProps = {
        playing: shouldPlay,
        muted: true,
        controls: true,
        playsInline: false,
        onPlay: () => {
            setShouldPlay(true);
        },
        trackPlayEvent: () => {
            tracker.log('Video', {
                url: new URL(window.location.href),
            });
        },
    };

    const videoType = Type.toLowerCase() as VidSrc['type'];

    return (
        <div ref={observerRef} className={css(S.VideoContainer)}>
            <div className={css(S.AspectRatioBox)}>
                <VideoPlayer
                    src={{
                        videoID: VideoID,
                        type: videoType,
                    }}
                    coverImage={{
                        src: mobile ? imgSrc : hiResImage,
                        width: mobile ? 378 : PRODUCT_GALLERY_SIZE,
                        height: mobile ? 378 : PRODUCT_GALLERY_SIZE,
                        alt: '',
                        onError: fallback,
                        variables: {
                            placeholderBackgroundColorLight: 'transparent',
                        },
                    }}
                    {...extraVideoProps}
                    variables={{
                        playIconSizeDesktop: 40,
                    }}
                    playIcon={mobile ? undefined : <span />}
                />
            </div>
        </div>
    );
};

export default VideoSlide;
